import { CheckedNotificationBatch, combineDeliveryChecks, NotificationBatchSendError, sendNotificationBatches, splitRecipients, BatchSendProgress } from "./notificationBatches";
import { loadApprovedTemplates } from "./loadApprovedTemplates";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";

import NotificationRecipientSelector from "./NotificationRecipientSelector";
import NotificationBenefitSelector from "./NotificationBenefitSelector";
import NotificationTemplateVisibility from "./NotificationTemplateVisibility";
import NotificationEventParticipants from "./NotificationEventParticipants";
import { BENEFIT_LABELS, benefitVariableValues, httpsPrefixedBenefitKeys, receiptEventVariableKey } from "./benefitTemplateBlueprints";
import type { NotificationBenefitKind } from "../../../api/services/notification-center.service";
import MessagePreview from "../../../Components/Messaging/MessagePreview";
import {
  Alert,
  Badge,
  Button,
  Col,
  Input,
  Label,
  Row,
  Spinner,
  Table,
} from "../../../Components/ui";
import {
  AlimTalkTemplate,
  NotificationCategory,
  NotificationChannel,
  NotificationDeliveryCheckResponse,
  notificationCenterService,
} from "../../../api/services/notification-center.service";
import {
  getAlimTalkTemplateContent,
  getAlimTalkTemplateName,
  getAlimTalkTemplateVariables,
  getUnresolvedAlimTalkVariables,
  isAutomaticAlimTalkVariable,
  renderAlimTalkTemplate,
} from "./alimtalkTemplate";

const categoryMeta: Array<{ value: NotificationCategory; label: string }> = [
  { value: "payment", label: "결제 관련" },
  { value: "usage", label: "이용관련" },
  { value: "site_notice", label: "지점 공지" },
  { value: "marketing", label: "마케팅" },
  { value: "mandatory", label: "필수 고지" },
];

const channelLabel: Record<NotificationChannel, string> = {
  web_push: "Web Push",
  app_push: "App Push",
  alimtalk: "알림톡",
};

const allChannels = Object.keys(channelLabel) as NotificationChannel[];

const formatMoney = (value: unknown, currency = "KRW") => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "-";
  return currency === "KRW"
    ? `${amount.toLocaleString("ko-KR")}원`
    : `${amount.toLocaleString("ko-KR")} ${currency}`;
};

type CheckedChannelSelection = {
  recipient_ids: number[];
  channels: NotificationChannel[];
  recipient_channel_overrides: Array<{
    recipient_id: number;
    channels: NotificationChannel[];
  }>;
};

const channelSelectionFor = (
  recipientIds: number[],
  selections: Record<number, NotificationChannel[]>,
): CheckedChannelSelection => {
  const recipientChannelOverrides = recipientIds.map((recipientId) => ({
    recipient_id: recipientId,
    channels: selections[recipientId] || [],
  }));
  return {
    recipient_ids: recipientIds,
    channels: [...new Set(recipientChannelOverrides.flatMap((item) => item.channels))],
    recipient_channel_overrides: recipientChannelOverrides,
  };
};

export default function NotificationSendComposer() {
  const [templates, setTemplates] = useState<AlimTalkTemplate[]>([]);
  const [visibleTemplateIds, setVisibleTemplateIds] = useState<number[]>([]);
  const [savingVisibility, setSavingVisibility] = useState(false);
  const templateGeneration = useRef(0);
  const [templateApiAvailable, setTemplateApiAvailable] = useState(true);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [recipientIds, setRecipientIds] = useState<number[]>([]);
  const [sendMode, setSendMode] = useState<"push" | "alimtalk">("alimtalk");
  const generation = useRef(0);
  const checkingRef = useRef(false);
  const sendingRef = useRef(false);
  const visibleChannels = allChannels.filter((channel) => sendMode === "alimtalk" ? channel === "alimtalk" : channel !== "alimtalk");
  const [category, setCategory] = useState<NotificationCategory>("site_notice");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [webUrl, setWebUrl] = useState("");
  const [appUrl, setAppUrl] = useState("");
  const [alimtalkTemplateId, setAlimtalkTemplateId] = useState<number | null>(null);
  const [alimtalkVariables, setAlimtalkVariables] = useState<Record<string, string>>({});
  const [deliveryCheck, setDeliveryCheck] = useState<NotificationDeliveryCheckResponse | null>(null);
  const [recipientChannels, setRecipientChannels] = useState<Record<number, NotificationChannel[]>>({});
  const [checkedBatches, setCheckedBatches] = useState<CheckedNotificationBatch[]>([]);
  const [batchProgress, setBatchProgress] = useState<BatchSendProgress | null>(null);
  const [sendNotice, setSendNotice] = useState("");
  const [lastParticipantEventName, setLastParticipantEventName] = useState("");
  const [participantRefresh, setParticipantRefresh] = useState(0);
  const [channelPage, setChannelPage] = useState(1);
  const [checkProgress, setCheckProgress] = useState("");
  const [preflightValid, setPreflightValid] = useState(false);
  const [checkingDelivery, setCheckingDelivery] = useState(false);
  const [sending, setSending] = useState(false);

  const loadTemplates = useCallback(async () => {
    const version = ++templateGeneration.current;
    setLoadingTemplates(true);
    try {
      const [templates, { data: setting }] = await Promise.all([
        loadApprovedTemplates(notificationCenterService.getAlimTalkTemplates),
        notificationCenterService.getComposerSetting(),
      ]);
      if (version !== templateGeneration.current) return;
      setTemplates(templates);
      setVisibleTemplateIds(setting.configured ? setting.visible_template_ids : templates.map((template) => template.id));
      setTemplateApiAvailable(true);
    } catch {
      if (version !== templateGeneration.current) return;
      setTemplates([]);
      setVisibleTemplateIds([]);
      setTemplateApiAvailable(false);
    } finally {
      if (version === templateGeneration.current) setLoadingTemplates(false);
    }
  }, []);

  // 서버 응답 전·실패 때도 잠김으로 본다(발송 0건, 결정 262).
  const [sendLocked, setSendLocked] = useState(true);
  useEffect(() => {
    let active = true;
    notificationCenterService.getSendLock()
      .then((response) => { if (active) setSendLocked(response.data.locked); })
      .catch(() => { if (active) setSendLocked(true); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    void loadTemplates();
    return () => { generation.current += 1; templateGeneration.current += 1; };
  }, [loadTemplates]);

  useEffect(() => {
    if (!sending) return;
    const preventClose = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", preventClose);
    return () => window.removeEventListener("beforeunload", preventClose);
  }, [sending]);

  const selectedTemplate = useMemo(() => (
    sendMode === "alimtalk" && visibleTemplateIds.includes(alimtalkTemplateId || 0) ? templates.find((template) => template.id === alimtalkTemplateId) || null : null
  ), [alimtalkTemplateId, templates, sendMode, visibleTemplateIds]);
  const templateVariables = useMemo(() => getAlimTalkTemplateVariables(selectedTemplate), [selectedTemplate]);
  const eventVariableKey = receiptEventVariableKey(templateVariables.map((variable) => variable.key));
  const participantEventName = eventVariableKey ? (alimtalkVariables[eventVariableKey] || "").trim().replace(/\s+/g, " ") : "";
  const participantNotice = (progress: BatchSendProgress) => progress.event_participation
    ? ` 참여 고객 ${progress.event_participation.new_count.toLocaleString()}명 신규 등록 · ${progress.event_participation.existing_count.toLocaleString()}명 기존 등록 확인.` : "";
  const httpsBenefitKeys = useMemo(() => httpsPrefixedBenefitKeys(selectedTemplate), [selectedTemplate]);
  const unresolvedVariables = useMemo(() => (
    getUnresolvedAlimTalkVariables(selectedTemplate, alimtalkVariables)
  ), [alimtalkVariables, selectedTemplate]);

  const resetDeliveryCheck = () => {
    generation.current += 1;
    setDeliveryCheck(null);
    setRecipientChannels({});
    setCheckedBatches([]);
    setPreflightValid(false);
  };

  const selectTemplate = (value: string) => {
    const nextId = value ? Number(value) : null;
    const nextTemplate = templates.find((template) => template.id === nextId) || null;
    setAlimtalkTemplateId(nextId);
    setAlimtalkVariables(Object.fromEntries(
      getAlimTalkTemplateVariables(nextTemplate)
        .filter((variable) => !isAutomaticAlimTalkVariable(variable))
        .map((variable) => [variable.key, ""]),
    ));
    resetDeliveryCheck();
  };

  const updateVariable = (key: string, value: string) => {
    setAlimtalkVariables((current) => ({ ...current, [key]: httpsBenefitKeys.includes(key) ? value.replace(/^https:\/\//i, "") : value }));
    resetDeliveryCheck();
  };

  const checkDelivery = async () => {
    if (checkingRef.current || sendingRef.current) return;
    const version = generation.current;
    if (!recipientIds.length) {
      toast.error("발송할 고객을 선택해 주세요.");
      return;
    }
    if (sendMode === "alimtalk" && !selectedTemplate) { toast.error("알림톡 승인 템플릿을 선택해 주세요."); return; }
    if (selectedTemplate && unresolvedVariables.length) {
      toast.error("알림톡 템플릿의 필수 변수를 모두 입력해 주세요.");
      return;
    }
    if (eventVariableKey && (!participantEventName || participantEventName.length > 200)) { toast.error("이벤트명을 1~200자로 입력해 주세요."); return; }
    setPreflightValid(false);
    setCheckedBatches([]);
    checkingRef.current = true;
    setCheckingDelivery(true);
    try {
      const hasCurrentSelection = Boolean(deliveryCheck && Object.keys(recipientChannels).length);
      const batches: CheckedNotificationBatch[] = [];
      const nextRecipientChannels: Record<number, NotificationChannel[]> = {};
      const recipientBatches = splitRecipients(recipientIds);
      for (const [index, ids] of recipientBatches.entries()) {
        setCheckProgress(`${index + 1} / ${recipientBatches.length} 묶음 확인 중`);
        const currentSelection = hasCurrentSelection ? channelSelectionFor(ids, recipientChannels) : null;
        const context = {
          category,
          alimtalk_template_id: selectedTemplate?.id || null,
          alimtalk_variables: selectedTemplate ? alimtalkVariables : {},
        };
        const { data: discoveryResult } = await notificationCenterService.checkDelivery({
          ...context,
          recipient_ids: ids,
          channels: currentSelection?.channels.length ? currentSelection.channels : visibleChannels,
          recipient_channel_overrides: currentSelection?.recipient_channel_overrides || [],
        });
        if (version !== generation.current) return;
        for (const recipient of discoveryResult.recipients || []) {
          nextRecipientChannels[recipient.id] = visibleChannels.filter((channel) => (
            recipient.site_registered && recipient.channels?.[channel]?.available
            && (!hasCurrentSelection || recipientChannels[recipient.id]?.includes(channel))
          ));
        }
        const selection = channelSelectionFor(ids, nextRecipientChannels);
        // Discovery may remove unavailable channels; sign only the final selection.
        const result = selection.channels.length
          ? (await notificationCenterService.checkDelivery({ ...context, ...selection })).data
          : discoveryResult;
        if (version !== generation.current) return;
        batches.push({ selection, result });
      }
      const combined = combineDeliveryChecks(batches);
      setRecipientChannels(nextRecipientChannels);
      setDeliveryCheck(combined);
      setCheckedBatches(batches);
      setChannelPage(1);
      const sendable = batches.filter((batch) => batch.selection.channels.length);
      setPreflightValid(sendable.length > 0 && sendable.every((batch) => Boolean(batch.result.preflight_token)));
      if (combined.recipients.some((recipient) => !recipient.site_registered)) {
        toast.error("현재 지점에 등록되지 않은 고객은 발송 대상에서 제외됩니다.");
      }
    } catch (error: any) {
      if (version === generation.current) toast.error(error?.response?.data?.detail || error?.message || "수신 가능 여부를 확인하지 못했습니다.");
    } finally {
      checkingRef.current = false;
      setCheckingDelivery(false);
      setCheckProgress("");
    }
  };

  const toggleRecipientChannel = (recipientId: number, channel: NotificationChannel) => {
    generation.current += 1;
    setRecipientChannels((current) => {
      const selected = current[recipientId] || [];
      return {
        ...current,
        [recipientId]: selected.includes(channel)
          ? selected.filter((item) => item !== channel)
          : [...selected, channel],
      };
    });
    setPreflightValid(false);
    setCheckedBatches([]);
  };

  const send = async () => {
    if (sendingRef.current || checkingRef.current) return;
    if (!deliveryCheck) {
      toast.error("수신 가능 여부를 먼저 확인해 주세요.");
      return;
    }
    const recipients = Array.isArray(deliveryCheck.recipients) ? deliveryCheck.recipients : [];
    const selectedRecipients = recipients.filter((recipient) => (
      recipient.site_registered && (recipientChannels[recipient.id] || []).length > 0
    ));
    if (!selectedRecipients.length) {
      toast.error("발송할 수신자와 채널을 하나 이상 선택해 주세요.");
      return;
    }
    const selectedChannels = [...new Set(
      selectedRecipients.flatMap((recipient) => recipientChannels[recipient.id] || []),
    )];
    if (selectedChannels.some((channel) => channel !== "alimtalk") && (!title.trim() || !body.trim())) {
      toast.error("Web/App 알림 제목과 내용을 입력해 주세요.");
      return;
    }
    const usesAlimTalk = selectedChannels.includes("alimtalk");
    if (usesAlimTalk && sendLocked) {
      toast.error("발송 잠김(시작 전) — 대표님 '시작' 뒤 열립니다.");
      return;
    }
    if (usesAlimTalk && (!selectedTemplate || unresolvedVariables.length)) {
      toast.error("알림톡 승인 템플릿과 필수 변수를 확인해 주세요.");
      return;
    }
    if (!preflightValid || !checkedBatches.length) {
      toast.error("수신자와 채널 정보를 다시 확인해 주세요.");
      return;
    }
    if (usesAlimTalk && deliveryCheck.credit_sufficient === false) {
      toast.error("알림톡 충전금이 부족합니다. 채널 설정에서 충전 요청 후 다시 확인해 주세요.");
      return;
    }
    sendingRef.current = true;
    setSending(true);
    const version = generation.current;
    try {
      setSendNotice("");
      const result = await sendNotificationBatches({
        batches: checkedBatches,
        content: {
          category,
          title: sendMode === "push" ? title.trim() : "",
          body: sendMode === "push" ? body.trim() : "",
          web_url: sendMode === "push" ? webUrl.trim() : "",
          app_url: sendMode === "push" ? appUrl.trim() : "",
          alimtalk_template_id: usesAlimTalk ? selectedTemplate?.id : null,
          alimtalk_variables: usesAlimTalk ? alimtalkVariables : {},
        },
        check: notificationCenterService.checkDelivery,
        send: notificationCenterService.send,
        onProgress: setBatchProgress,
        shouldContinue: () => version === generation.current,
      });
      const notice = `${result.total.toLocaleString()}개 묶음 · ${result.created.toLocaleString()}명 발송 접수 완료.${participantNotice(result)} 최종 결과는 통합 내역에서 확인해 주세요.`;
      if (result.event_participation) { setLastParticipantEventName(result.event_participation.event_name); setParticipantRefresh((current) => current + 1); }
      toast.success(notice);
      setSendNotice(notice);
      setRecipientIds([]);
      setTitle("");
      setBody("");
      setWebUrl("");
      setAppUrl("");
      setAlimtalkTemplateId(null);
      setAlimtalkVariables({});
      resetDeliveryCheck();
    } catch (error: any) {
      const cause = error instanceof NotificationBatchSendError ? error.cause : error;
      const detail = (cause as any)?.response?.data?.detail || (cause as any)?.message || "알림 발송에 실패했습니다.";
      if (error instanceof NotificationBatchSendError) {
        if (error.progress.event_participation) setLastParticipantEventName(error.progress.event_participation.event_name);
        setParticipantRefresh((current) => current + 1);
        setRecipientIds(error.remainingIds);
        resetDeliveryCheck();
        setSendNotice(`${error.progress.completed} / ${error.progress.total}개 묶음 · ${error.progress.created.toLocaleString()}명 접수 후 중단되었습니다.${participantNotice(error.progress)} ${detail} ${error.uncertainIds.length ? `방금 요청한 ${error.uncertainIds.length.toLocaleString()}명 묶음은 통합 내역에서 접수 여부를 확인해 주세요. 중복 발송 방지를 위해 선택에서 제외했습니다. ` : ""}아직 요청하지 않은 ${error.remainingIds.length.toLocaleString()}명만 선택되어 있습니다.`);
      }
      toast.error(detail);
    } finally {
      sendingRef.current = false;
      setSending(false);
      setBatchProgress(null);
    }
  };

  const previewValues = {
      ...Object.fromEntries(templateVariables
        .filter(isAutomaticAlimTalkVariable)
        .map((variable) => [variable.key, `[${variable.label || "수신자별 자동 입력"}]`])),
      ...alimtalkVariables,
  };
  const preview = deliveryCheck?.server_preview?.content
    || deliveryCheck?.alimtalk_preview
    || renderAlimTalkTemplate(getAlimTalkTemplateContent(selectedTemplate), previewValues);
  const previewButtons = deliveryCheck?.server_preview?.buttons || (selectedTemplate?.buttons || []).map((button) => ({
    ...button,
    url_mobile: button.url_mobile ? renderAlimTalkTemplate(button.url_mobile, previewValues) : undefined,
    url_pc: button.url_pc ? renderAlimTalkTemplate(button.url_pc, previewValues) : undefined,
  }));
  const estimatedCharge = deliveryCheck?.max_estimated_charge
    ?? deliveryCheck?.alimtalk_cost?.estimated_amount;
  const unitPrice = deliveryCheck?.unit_price ?? deliveryCheck?.alimtalk_cost?.unit_price;
  const currency = deliveryCheck?.wallet?.currency || deliveryCheck?.alimtalk_balance?.currency || "KRW";

  return (
    <fieldset disabled={sending || savingVisibility} className="min-w-0">
      <div role="tablist" aria-label="발송 방식" className="d-flex gap-2 mb-4">
        {([['alimtalk', '알림톡'], ['push', '통합 메시지']] as const).map(([mode, label]) => <Button key={mode} id={`send-tab-${mode}`} role="tab" aria-controls="send-panel" tabIndex={sendMode === mode ? 0 : -1} aria-selected={sendMode === mode} onKeyDown={(event) => { if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) { event.preventDefault(); const next = event.key === "Home" ? "alimtalk" : event.key === "End" ? "push" : sendMode === "push" ? "alimtalk" : "push"; setSendMode(next); resetDeliveryCheck(); document.getElementById(`send-tab-${next}`)?.focus(); } }} color={sendMode === mode ? "primary" : "soft-primary"} onClick={() => { setSendMode(mode); resetDeliveryCheck(); }}>{label}</Button>)}
      </div>
    <div role="tabpanel" id="send-panel" aria-labelledby={`send-tab-${sendMode}`}>
    {sendMode === "alimtalk" && sendLocked ? <Alert color="warning" role="status">발송 잠김(시작 전) — 대표님 '시작' 뒤 열립니다. 템플릿·미리보기·발송 내역은 지금도 볼 수 있습니다.</Alert> : null}
    {sendNotice ? <Alert color="info">{sendNotice} <a href="/notification-center/history">통합 내역 보기</a></Alert> : null}
    <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
      <div className="min-w-0">
        {lastParticipantEventName && !eventVariableKey ? <NotificationEventParticipants key={lastParticipantEventName} eventName={lastParticipantEventName} refreshKey={participantRefresh} /> : null}
        <NotificationRecipientSelector value={recipientIds} disabled={sending} onChange={(ids) => { setRecipientIds(ids); resetDeliveryCheck(); }} />

        <Row className="g-3 mb-3">
          <Col md={5}>
            <Label htmlFor="notification-category">메시지 분류</Label>
            <Input id="notification-category" type="select" value={category} onChange={(event) => { setCategory(event.target.value as NotificationCategory); resetDeliveryCheck(); }}>
              {categoryMeta.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </Input>
          </Col>
          {sendMode === "alimtalk" ? <Col md={7}>
            <Label htmlFor="alimtalk-template">알림톡 승인 템플릿</Label>
            <Input id="alimtalk-template" type="select" value={alimtalkTemplateId || ""} onChange={(event) => selectTemplate(event.target.value)} disabled={loadingTemplates || !templateApiAvailable}>
              <option value="">승인 템플릿 선택</option>
              {templates.filter((template) => visibleTemplateIds.includes(template.id)).map((template) => (
                <option key={template.id} value={template.id}>
                  {getAlimTalkTemplateName(template) || `템플릿 ${template.id}`} ({template.template_code || template.id})
                </option>
              ))}
            </Input>
          </Col> : null}
        </Row>

        {sendMode === "alimtalk" && !templateApiAvailable ? <Alert color="warning">알림톡 템플릿 또는 표시 설정을 불러오지 못했습니다. <Button size="sm" onClick={() => void loadTemplates()} disabled={loadingTemplates}>다시 불러오기</Button></Alert> : null}
        {sendMode === "alimtalk" && templateApiAvailable && !loadingTemplates && templates.length === 0 ? <Alert color="info">사용할 수 있는 승인 알림톡 템플릿이 없습니다.</Alert> : null}
        {sendMode === "alimtalk" && templateApiAvailable && !loadingTemplates ? <>
          {templates.length > 0 && !templates.some((template) => visibleTemplateIds.includes(template.id)) ? <Alert color="info">표시할 템플릿이 없습니다. 아래에서 사용할 템플릿을 선택해 주세요.</Alert> : null}
          <NotificationTemplateVisibility templates={templates} visibleIds={visibleTemplateIds.filter((id) => templates.some((template) => template.id === id))} onSavingChange={(saving) => { setSavingVisibility(saving); if (saving) resetDeliveryCheck(); }} onSaved={(ids) => {
            setVisibleTemplateIds(ids);
            if (alimtalkTemplateId && !ids.includes(alimtalkTemplateId)) selectTemplate("");
            resetDeliveryCheck();
          }} />
        </> : null}

        {selectedTemplate ? (
          <div className="border rounded p-3 mb-3">
            <div className="d-flex flex-wrap justify-content-between gap-2 mb-3">
              <div><h6 className="mb-1">템플릿 입력 데이터</h6><p className="text-muted small mb-0">고객명·연락처·지점명·서브도메인은 자동 입력됩니다. 나머지 항목만 입력해 주세요.</p></div>
              <Badge color="light">{String(selectedTemplate.scope || "SITE").toUpperCase() === "HEADQUARTERS" ? "본사 템플릿" : "지점 템플릿"}</Badge>
            </div>
            {eventVariableKey ? <>
              <Alert color="info">실제 참여 완료를 확인한 고객을 선택해 주세요. 알림톡 발송 접수 시 참여 명부에 자동 등록됩니다. 같은 지점의 같은 이벤트명은 하나의 명부로 관리하며, 이미 등록된 고객은 중복 등록하지 않습니다.</Alert>
              {participantEventName && participantEventName.length <= 200 ? <NotificationEventParticipants key={participantEventName} eventName={participantEventName} refreshKey={participantRefresh} /> : null}
            </> : null}
            {(Object.keys(BENEFIT_LABELS) as NotificationBenefitKind[]).filter((kind) =>
              Object.keys(benefitVariableValues(templateVariables.map((variable) => variable.key), kind)).length > 0
            ).map((kind) => <div key={`${selectedTemplate.id}:${kind}`}>
              <p className="small text-muted">이벤트 참여를 완료하여 혜택 수령 대상이 된 고객만 선택해 주세요.</p>
              <NotificationBenefitSelector kind={kind} variableKeys={templateVariables.map((variable) => variable.key)} values={alimtalkVariables} httpsKeys={httpsBenefitKeys}
                onChange={(updates) => { setAlimtalkVariables((current) => ({ ...current, ...updates })); resetDeliveryCheck(); }} />
            </div>)}
            {templateVariables.filter((variable) => !isAutomaticAlimTalkVariable(variable)).length === 0 ? <div className="text-muted small">입력할 변수가 없는 템플릿입니다.</div> : null}
            <Row className="g-3">
              {templateVariables.filter((variable) => !isAutomaticAlimTalkVariable(variable)).map((variable) => (
                <Col md={6} key={variable.key}>
                  <Label htmlFor={`alimtalk-variable-${variable.key}`}>
                    {variable.label || variable.key}{variable.required !== false && !isAutomaticAlimTalkVariable(variable) ? <span className="text-danger"> *</span> : null}
                  </Label>
                  <Input
                    id={`alimtalk-variable-${variable.key}`}
                    value={isAutomaticAlimTalkVariable(variable) ? "수신자별 자동 입력" : alimtalkVariables[variable.key] || ""}
                    onChange={(event) => updateVariable(variable.key, event.target.value)}
                    placeholder={variable.description || `#{${variable.key}}에 들어갈 값`}
                    disabled={isAutomaticAlimTalkVariable(variable)}
                  />
                  {isAutomaticAlimTalkVariable(variable) ? <div className="form-text">수신자와 현재 지점 정보를 기준으로 서버에서 안전하게 입력합니다.</div> : null}
                  {httpsBenefitKeys.includes(variable.key) ? <div className="form-text">받기 버튼의 https:// 뒤에 들어갈 주소입니다. 전체 HTTPS 주소를 붙여넣어도 됩니다.</div> : null}
                </Col>
              ))}
            </Row>
          </div>
        ) : null}

        {sendMode === "push" ? <>
        <div className="border rounded p-3 mb-3">
          <h6 className="mb-3">Web/App 알림 내용</h6>
          <div className="mb-3"><Label htmlFor="notification-title">제목</Label><Input id="notification-title" value={title} onChange={(event) => { setTitle(event.target.value); resetDeliveryCheck(); }} maxLength={200} /></div>
          <div><Label htmlFor="notification-body">내용</Label><Input id="notification-body" type="textarea" rows={5} value={body} onChange={(event) => { setBody(event.target.value); resetDeliveryCheck(); }} maxLength={5000} /></div>
        </div>

        <Row className="g-3">
          <Col md={6}><Label htmlFor="notification-web-url">Web 이동 경로</Label><Input id="notification-web-url" value={webUrl} onChange={(event) => { setWebUrl(event.target.value); resetDeliveryCheck(); }} placeholder="/notification 또는 https://..." /></Col>
          <Col md={6}><Label htmlFor="notification-app-url">App 이동 경로</Label><Input id="notification-app-url" value={appUrl} onChange={(event) => { setAppUrl(event.target.value); resetDeliveryCheck(); }} placeholder="amazingcamp://..." /></Col>
        </Row>
        </> : null}
        <div className="rounded border p-3 mt-4">
          <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
            <h5 className="mb-0">수신자별 채널</h5>
            <Button color="soft-primary" size="sm" onClick={() => void checkDelivery()} disabled={checkingDelivery}>
              {checkingDelivery ? <><Spinner size="sm" className="me-1" />{checkProgress || "확인 중"}</> : deliveryCheck && !preflightValid ? "변경사항 다시 확인" : "수신 가능 여부 확인"}
            </Button>
          </div>
          {!deliveryCheck ? <p className="text-muted small mb-0">템플릿·변수·수신자를 확정한 뒤 확인하세요. 입력을 바꾸면 다시 확인해야 합니다.</p> : null}
          {deliveryCheck ? (
            <>
              <p className="small text-muted">조회 {deliveryCheck.recipients.length.toLocaleString()}명 · 채널 선택 {Object.values(recipientChannels).filter((channels) => channels.length).length.toLocaleString()}명</p>
              <div className="border rounded p-2 mb-3 small">
                {visibleChannels.map((channel) => {
                  const status = deliveryCheck.site_channels?.[channel];
                  return <div key={channel} className="mb-1"><strong>{channelLabel[channel]}</strong> · {status?.site_enabled && status?.configured ? "사용 가능" : status?.reason || "설정 확인 필요"}</div>;
                })}
              </div>

              {deliveryCheck.preview_error ? <Alert color="danger">{deliveryCheck.preview_error}</Alert> : null}
              {!preflightValid ? <Alert color="warning">채널 선택이 변경되었습니다. 발송 전에 변경사항을 다시 확인해 주세요.</Alert> : null}

              {selectedTemplate && estimatedCharge !== undefined && estimatedCharge !== null ? (
                <Alert color={deliveryCheck.credit_sufficient === false ? "danger" : "info"}>
                  최대 예상 차감액 <strong>{formatMoney(estimatedCharge, currency)}</strong>
                  {unitPrice !== undefined && unitPrice !== null ? <span className="ms-2">(건당 {formatMoney(unitPrice, currency)})</span> : null}
                  {deliveryCheck.credit_sufficient === false ? <div className="mt-1">충전금이 부족하여 알림톡을 발송할 수 없습니다.</div> : null}
                </Alert>
              ) : null}

              <div className="table-responsive border rounded max-h-96">
                <Table className="align-middle mb-0 small">
                  <thead><tr><th>수신자</th>{visibleChannels.map((channel) => <th className="text-center" key={channel}>{channelLabel[channel]}</th>)}</tr></thead>
                  <tbody>
                    {(deliveryCheck.recipients || []).slice((channelPage - 1) * 50, channelPage * 50).map((recipient) => (
                      <tr key={recipient.id}>
                        <td>
                          {recipient.site_registered
                            ? <>{recipient.name || `고객 ${recipient.id}`}<br /><small className="text-muted">{recipient.phone_num || recipient.reason || "-"}</small></>
                            : <span className="text-danger">고객 {recipient.id}<br /><small>{recipient.reason}</small></span>}
                        </td>
                        {visibleChannels.map((channel) => {
                          const availability = recipient.channels?.[channel];
                          const selected = (recipientChannels[recipient.id] || []).includes(channel);
                          return (
                            <td className="text-center" key={channel}>
                              <Input className="form-check-input m-0" type="checkbox" aria-label={`${recipient.id} ${channelLabel[channel]} 사용`} checked={selected} disabled={!availability?.available} onChange={() => toggleRecipientChannel(recipient.id, channel)} />
                              {!availability?.available ? <div className="text-muted mt-1" style={{ fontSize: 11 }}>{availability?.reason || "사용 불가"}</div> : null}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              {deliveryCheck.recipients.length > 50 ? <div className="d-flex gap-2 align-items-center mt-2">
                <Button size="sm" disabled={channelPage <= 1} onClick={() => setChannelPage(channelPage - 1)}>이전 수신자</Button>
                <span>{channelPage} / {Math.ceil(deliveryCheck.recipients.length / 50)}</span>
                <Button size="sm" disabled={channelPage * 50 >= deliveryCheck.recipients.length} onClick={() => setChannelPage(channelPage + 1)}>다음 수신자</Button>
              </div> : null}
            </>
          ) : null}
          {recipientIds.length >= 3000 ? <Alert color="info" className="mt-3">3,000명 단위로 자동 분할 발송됩니다. 선택 고객 {recipientIds.length.toLocaleString()}명 · {splitRecipients(recipientIds).length}개 묶음. 발송 접수가 완료될 때까지 이 화면을 유지해 주세요.</Alert> : null}
          {batchProgress ? <p role="status">{batchProgress.completed} / {batchProgress.total}개 묶음 접수 · {batchProgress.created.toLocaleString()}명</p> : null}
          <Button color="primary" className="w-100 mt-4" onClick={() => void send()} disabled={sending || checkingDelivery || !deliveryCheck || !preflightValid || deliveryCheck.credit_sufficient === false || (sendMode === "alimtalk" && sendLocked)}>
            {sending ? <><Spinner size="sm" className="me-2" />발송 중</> : sendMode === "alimtalk" && sendLocked ? "발송 잠김(시작 전)" : "알림 발송"}
          </Button>
        </div>
      </div>
      <aside aria-label="메시지 미리보기" className="min-w-0 lg:sticky lg:top-24">
        {sendMode === "alimtalk" ? (selectedTemplate ? <MessagePreview
          activeTab="alimtalk"
          message={preview}
          buttons={previewButtons}
          siteName="알림톡"
        /> : <div className="rounded border p-4"><h5>메시지 미리보기</h5><p className="text-muted mb-0">승인 템플릿을 선택하면 미리보기가 표시됩니다.</p></div>) : <div className="rounded border p-4">
          <h5>메시지 미리보기</h5><div className="rounded border p-3 mt-3"><strong>{title || "알림 제목"}</strong><p className="whitespace-pre-wrap break-words mt-2 mb-0">{body || "알림 내용을 입력해 주세요."}</p></div>
        </div>}
      </aside>
    </div>
    </div>
    </fieldset>
  );
}
